import { StyleSheet, css } from 'aphrodite'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { useUser } from '../contexts/UserContext'
import { userService } from '../services/userService'
import { type Company, StakeholderType, type UserType } from '../types/user'
import { getUserTypeDisplayName } from '../utils/profileErrors'

const ProfileSwitcher: React.FC = () => {
  const { user, userProfiles, switchProfile, isProfileSwitching } = useUser()
  const [isOpen, setIsOpen] = useState(false)
  const [company, setCompany] = useState<{
    name: string
    address: string
    logo_url: string | null
  } | null>(null)
  const lastFetchedUserIdRef = useRef<number | null>(null)

  // Fetch company data (ref guard prevents StrictMode double-fetch)
  useEffect(() => {
    if (!user?.id || lastFetchedUserIdRef.current === user.id) return
    lastFetchedUserIdRef.current = user.id

    const fetchCompany = async () => {
      try {
        const companyData = await userService.getUserCompanyPublic(user.id)
        setCompany(companyData)
      } catch (error) {
        console.error('Failed to fetch company data:', error)
        setCompany(null)
      }
    }

    fetchCompany()
  }, [user?.id])

  const getUserDisplayName = () => {
    const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
    const userType = user?.user_type
      ? getUserTypeDisplayName(user.user_type as UserType)
      : 'User'

    if (!fullName) {
      return `Current User (${userType})`
    }
    return `${fullName} (${userType})`
  }

  const getStakeholderDisplayName = (type: string) => {
    const stakeholderType = (() => {
      switch (type) {
        case StakeholderType.CONTRACTOR:
          return 'Contractor'
        case StakeholderType.BROKER:
          return 'Broker'
        case StakeholderType.SUB_HAULER:
          return 'Sub Hauler'
        default:
          return type
      }
    })()

    const companyName = company?.name

    if (companyName) {
      const result = `${companyName} (${stakeholderType})`
      return result
    }
    return stakeholderType
  }

  const getStakeholderIcon = (type: string) => {
    switch (type) {
      case StakeholderType.CONTRACTOR:
        return 'fa-hard-hat'
      case StakeholderType.BROKER:
        return 'fa-handshake'
      case StakeholderType.SUB_HAULER:
        return 'fa-route'
      default:
        return 'fa-user'
    }
  }

  // Debug: Always show for now to test the component
  if (!user) {
    return (
      <div className={css(styles.container)}>
        <div className={css(styles.divider)} />
        <div className={css(styles.profileSwitcher)}>
          <div style={{ color: '#fff', padding: '12px', fontSize: '12px' }}>
            No user data
          </div>
        </div>
      </div>
    )
  }

  if (!userProfiles || userProfiles.length === 0) {
    return (
      <div className={css(styles.container)}>
        <div className={css(styles.divider)} />
        <div className={css(styles.profileSwitcher)}>
          <div style={{ color: '#fff', padding: '12px', fontSize: '12px' }}>
            No profiles loaded (userProfiles:{' '}
            {userProfiles ? userProfiles.length : 'null'})
          </div>
        </div>
      </div>
    )
  }

  if (userProfiles.length <= 1) {
    const singleProfile = userProfiles[0]
    return (
      <div className={css(styles.container)}>
        <div className={css(styles.divider)} />
        <div className={css(styles.profileSwitcher)}>
          <div className={css(styles.singleProfile)}>
            <div className={css(styles.profileInfo)}>
              <i
                className={`fa-solid ${getStakeholderIcon(singleProfile?.stakeholder_type || '')} ${css(styles.profileIcon)}`}
              />
              <div>
                <div className={css(styles.profileName)}>
                  {getStakeholderDisplayName(
                    singleProfile?.stakeholder_type || '',
                  )}
                </div>
                <div className={css(styles.profileSubtext)}>
                  {getUserDisplayName()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleProfileSwitch = async (profileId: number) => {
    try {
      await switchProfile(profileId)
      setIsOpen(false)
      toast.success('Profile switched successfully')
    } catch (error) {
      console.error('Failed to switch profile:', error)
      const errorMessage =
        (error as Error)?.message ||
        'Failed to switch profile. Please try again.'
      if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        toast.error('Profile not found. Please refresh the page and try again.')
      } else {
        toast.error(errorMessage)
      }
    }
  }

  const currentProfile = user.current_profile

  if (!currentProfile) {
    return (
      <div className={css(styles.container)}>
        <div className={css(styles.divider)} />
        <div className={css(styles.profileSwitcher)}>
          <div style={{ color: '#fff', padding: '12px', fontSize: '12px' }}>
            No current_profile in user data
          </div>
        </div>
      </div>
    )
  }

  const otherProfiles = userProfiles.filter((p) => p.id !== currentProfile.id)

  return (
    <div className={css(styles.container)}>
      <div className={css(styles.divider)} />
      <div className={css(styles.profileSwitcher)}>
        <div className={css(styles.profileContainer)}>
          <div className={css(styles.profileButton)}>
            <div className={css(styles.profileInfo)}>
              <i
                className={`fa-solid ${getStakeholderIcon(currentProfile.stakeholder_type)} ${css(styles.profileIcon)}`}
              />
              <div>
                <div className={css(styles.profileName)}>
                  {getStakeholderDisplayName(currentProfile.stakeholder_type)}
                </div>
                <div className={css(styles.profileSubtext)}>
                  {getUserDisplayName()}
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={css(styles.dropdownToggle)}
            disabled={isProfileSwitching}
          >
            <i
              className={`fa-solid ${isOpen ? 'fa-chevron-up' : 'fa-chevron-down'} ${css(styles.chevron)}`}
            />
          </button>
        </div>

        {isOpen && (
          <div className={css(styles.dropdown)}>
            {otherProfiles.map((profile) => (
              <button
                key={profile.id}
                onClick={() => handleProfileSwitch(profile.id)}
                className={css(styles.profileOption)}
                disabled={isProfileSwitching}
              >
                <i
                  className={`fa-solid ${getStakeholderIcon(profile.stakeholder_type)} ${css(styles.profileIcon)}`}
                />
                <span>
                  {getStakeholderDisplayName(profile.stakeholder_type)}
                </span>
              </button>
            ))}
          </div>
        )}

        {isProfileSwitching && (
          <div className={css(styles.loadingOverlay)}>
            <i className="fa-solid fa-spinner fa-spin" />
            <span>Switching...</span>
          </div>
        )}
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    marginTop: 'auto',
  },
  divider: {
    height: '1px',
    backgroundColor: '#374151',
  },
  profileSwitcher: {
    position: 'relative',
    padding: '8px',
  },
  currentProfile: {
    width: '100%',
    background: 'none',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    padding: '12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#374151',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  singleProfile: {
    width: '100%',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  profileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '18px',
  },
  profileIcon: {
    width: '16px',
    opacity: 0.8,
  },
  profileName: {
    fontSize: '16px',
    fontWeight: 500,
    textAlign: 'left',
  },
  profileSubtext: {
    fontSize: '14px',
    opacity: 0.7,
    textAlign: 'left',
  },
  chevron: {
    fontSize: '14px',
    opacity: 0.6,
  },
  settingsIcon: {
    fontSize: '16px',
    opacity: 0.6,
  },
  profileContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  profileButton: {
    flex: 1,
    color: '#fff',
    padding: '12px',
    borderRadius: '8px 0 0 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dropdownToggle: {
    background: 'none',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    padding: '12px 8px',
    borderRadius: '0 8px 8px 0',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#374151',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  dropdown: {
    position: 'absolute',
    bottom: '100%',
    left: '8px',
    right: '8px',
    backgroundColor: '#1F2A37',
    border: '1px solid #374151',
    borderRadius: '8px',
    marginBottom: '8px',
    boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
    zIndex: 1000,
  },
  profileOption: {
    width: '100%',
    background: 'none',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    padding: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    transition: 'background-color 0.2s',
    fontSize: '14px',
    ':hover': {
      backgroundColor: '#374151',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    ':first-child': {
      borderRadius: '8px 8px 0 0',
    },
    ':last-child': {
      borderRadius: '0 0 8px 8px',
    },
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(31, 42, 55, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
  },
})

export default ProfileSwitcher
